import { useMemo, useState } from 'react';
import './index.css';
import { NoData } from '../../widgets/WidgetStates';

interface IBarChartProps {
  legend: boolean;
  data?: any;
  meta?: any;
}

function BarChartHorizontal({ legend, data, meta }: IBarChartProps) {
  // State to manage tooltip data when hovering over bars
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    label: string;
    value: number;
    color: string;
    position: { x: number; y: number };
  } | null>(null);

  const maxTotal = useMemo(() => {
    let maxLabelTotal = 0;
    data?.labels.forEach((label: string, labelIndex: number) => {
      const labelTotal = data.datasets.reduce(
        (acc: number, dataset: any) => acc + dataset.data[labelIndex],
        0,
      );
      if (labelTotal > maxLabelTotal) {
        maxLabelTotal = labelTotal;
      }
    });
    return maxLabelTotal;
  }, [data]);

  const sortedData = useMemo(() => {
    if (!data) return null;

    // Calculate totals for each label
    const totals = data.labels.map((label: string, index: number) =>
      data.datasets.reduce(
        (sum: number, dataset: any) => sum + dataset.data[index],
        0,
      ),
    );

    // Create an array of indices from 0 to labels.length, sort it according to totals
    const sortedIndices = totals
      .map((total: number, index: number) => ({ index, total }))
      .sort((a: any, b: any) => b.total - a.total)
      .map((item: any) => item.index);

    // Sort labels and datasets according to sortedIndices
    return {
      labels: meta.preserveOrder
        ? data.labels
        : sortedIndices.map((index: number) => data.labels[index]),
      datasets: meta.preserveOrder
        ? data.datasets
        : data.datasets.map((dataset: any) => ({
            ...dataset,
            data: sortedIndices.map((index: number) => dataset.data[index]),
          })),
    };
  }, [data]);

  const updateHoverToolTipAttributes = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    dataset: any,
    labelIndex: number,
  ) => {
    if (legend) {
      const rect = e.currentTarget.getBoundingClientRect();
      const parentRect = e.currentTarget
        .closest('.intl-overflow-widget')
        ?.getBoundingClientRect();
      setHoveredTooltip({
        label: dataset?.label,
        value: dataset.data[labelIndex],
        color: dataset.backgroundColor,
        position: {
          x: rect.left + rect.width / 2 - (parentRect?.left || 0),
          y: rect.top - (parentRect?.top || 0),
        },
      });
    }
  };

  return data == null ? (
    <NoData />
  ) : (
    <div className="intl-overflow-widget relative rounded-2xl">
      {/* Tooltip rendered outside overflow container */}
      {legend && hoveredTooltip && (
        <div
          className="border-black-200 text-grey-800 absolute z-[9999] flex h-9 w-fit items-center justify-center gap-x-1 rounded-lg border bg-white px-3 py-2 whitespace-nowrap shadow-[0px_12px_16px_-4px_#10182814]"
          style={{
            left: `${hoveredTooltip.position.x}px`,
            top: `${hoveredTooltip.position.y}px`,
            transform: 'translate(-50%, -100%)',
            marginTop: '-12px',
          }}
        >
          <div className="clip-top absolute right-0 -bottom-[7px] left-0 m-auto h-2 w-4 bg-white"></div>
          <span
            className="text-black-600 block h-2 w-2 rounded-full text-sm"
            style={{
              backgroundColor: hoveredTooltip.color,
            }}
          />
          <p className="flex items-center gap-x-1">
            <span className="text-grey-500 text-xs">
              {hoveredTooltip.label}
            </span>
            <span className="text-grey-900 text-xs font-semibold">
              {hoveredTooltip.value}
            </span>
          </p>
        </div>
      )}

      <div
        className={`relative max-h-[366px] min-w-[550px] overflow-auto px-6`}
      >
        <div className="flex flex-col gap-y-6 pb-8">
          {sortedData?.labels.map((label: string, labelIndex: number) => {
            // Calculate total for the current label
            const total = sortedData.datasets.reduce(
              (acc: number, dataset: any) => acc + dataset.data[labelIndex],
              0,
            );

            return (
              <div key={label} className="flex items-center gap-x-4">
                <span
                  className="text-black-600 w-[131px] truncate text-right text-xs"
                  title={label}
                >
                  {label}
                </span>

                <div className="relative !flex w-full items-center gap-x-[2px] rounded-full bg-white">
                  {sortedData?.datasets.map(
                    (dataset: any, datasetIndex: number) => {
                      // const barWidth =
                      //   total === maxTotal
                      //     ? 100
                      //     : (dataset.data[labelIndex] / maxTotal) * 100;
                      return (
                        <>
                          {dataset.data[labelIndex] > 0 && (
                            <div
                              key={datasetIndex}
                              className="intl-widget-ignore-element group relative flex h-5 flex-row items-center justify-end pr-1 text-xs font-semibold text-white transition-[width]"
                              onMouseEnter={(e) => {
                                updateHoverToolTipAttributes(
                                  e,
                                  dataset,
                                  labelIndex,
                                );
                              }}
                              onMouseLeave={() => setHoveredTooltip(null)}
                              style={{
                                width: `${
                                  (dataset.data[labelIndex] / maxTotal) * 100
                                }%`,
                                backgroundColor: dataset.backgroundColor,
                                zIndex: data.datasets.length - datasetIndex,
                              }}
                            ></div>
                          )}
                        </>
                      );
                    },
                  )}
                  <span
                    className={`${
                      total !== 0 && 'ml-1'
                    } text-grey-800 text-xs font-medium`}
                  >
                    {total == 0 ? (
                      <>
                        <div className="intl-widget-ignore-element bg-black-400 h-5 w-[3px]"></div>
                        <span
                          className={`text-black-900 absolute inset-0 left-3 flex items-center text-sm font-medium`}
                        >
                          {total}
                        </span>
                      </>
                    ) : (
                      total
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {legend && (
        <div className="legend-container text-black-600 sticky top-0 z-[9] flex w-full flex-wrap justify-center gap-x-4 rounded-2xl bg-white pt-5 pb-8 text-sm md:ml-0">
          {data?.datasets.map((dataset: any, index: number) => (
            <div key={dataset?.label} className={`flex items-center gap-x-2`}>
              <span
                className="intl-widget-ignore-element text-black-600 h-2 w-2 rounded-full text-sm"
                style={{ backgroundColor: dataset.backgroundColor }}
              />
              <span className="capitalize">{dataset.label?.toLowerCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BarChartHorizontal;
